import { useMemo } from 'react';
import { useQuery, type QueryClient } from '@tanstack/react-query';

import { calendarApi, type LiveLesson } from '@/api/calendar';
import type { ApiError } from '@/api/client';
import { usersApi } from '@/api/users';
import { showLive, showRooms } from '@/lib/calendarAccess';
import { buildCalItems, type CalItem } from '@/lib/calendarItems';
import { useAuthStore } from '@/stores/auth';

import { useAppConfig } from './useAppConfig';

/** Общий префикс ключей календаря ученика: события платформы и личные записи. */
export const STUDENT_CALENDAR_KEY = ['student', 'calendar'] as const;

/**
 * Сброс календаря после создания, правки и удаления личной записи.
 *
 * ⚠️ ГЛАВНАЯ ЧИТАЕТ ТЕ ЖЕ КЛЮЧИ, ЧТО СТРАНИЦА РАСПИСАНИЯ (02.10.2026). Раньше у главной был свой ключ
 * `week-events`, который не сбрасывал никто: запись появлялась в расписании, а недельная строка главной
 * показывала прежнее до перезагрузки. Отдельного ключа больше нет — сброс по префиксу достаёт оба экрана.
 */
export function invalidateStudentCalendar(qc: QueryClient) {
  return qc.invalidateQueries({ queryKey: STUDENT_CALENDAR_KEY });
}

/**
 * Календарь ученика: три запроса и один список. Зовут страница расписания и недельные виджеты главной —
 * кэш у них общий, второй волны запросов при переходе с главной в расписание нет.
 */
export function useCalendarItems() {
  const user = useAuthStore((st) => st.user);
  // Живые уроки и комнаты — по разделу и выключателю, одним правилом на событие, кнопку и запрос
  // (`lib/calendarAccess.ts`). Перечитывание настроек с сервера перерисовывает экран — `useAppConfig`.
  useAppConfig();
  const live = showLive(user);
  const rooms = showRooms(user);

  // Потоки ученика — из ЗАПИСЕЙ (включая закрытые курсы): по ним запрашиваются живые уроки и события платформы.
  const enrollments = useQuery({
    queryKey: ['student', 'enrollments'],
    queryFn: () => usersApi.myEnrollments(),
  });
  const marathonIds = useMemo(
    () => (enrollments.data ?? []).map((e) => e.marathon_id),
    [enrollments.data],
  );

  // ⚠️ БЕЗ РАЗДЕЛА ЗАПРОС НЕ УХОДИТ (02.10.2026). Живые уроки закрыты правом `live`: calendar-service отвечает
  // 403 ученику без него. Раньше запрос шёл всем, отказ глотался здесь же — и оставался в трафике (от таких
  // отказов уходили 19.09.2026: ревьюер Apple видит сеть). Отказ у ученика с разделом по-прежнему не ошибка
  // экрана: уроков просто нет, остальное рисуется как обычно.
  //
  // ⚠️ Карточки «Live-уроки пока не открыты» с замком НЕТ (решение владельца 14.09.2026, Apple 3.1.3(f)):
  // признак `locked` в ответе остаётся, но ученику про закрытое ничего не показывается.
  const lessons = useQuery({
    queryKey: ['student', 'live-lessons', marathonIds],
    enabled: live && enrollments.isSuccess,
    queryFn: async () => {
      let locked = false;
      const results = await Promise.all(
        marathonIds.map((id) =>
          calendarApi.liveLessonsForMarathon(id).catch((err: unknown) => {
            if ((err as ApiError | undefined)?.status === 403) locked = true;
            return [] as LiveLesson[];
          }),
        ),
      );
      return { items: results.flat(), locked };
    },
  });

  // События платформы, адресованные ученику (всем + его потокам). Сюда же сервер подмешивает разговорные комнаты.
  const platform = useQuery({
    queryKey: [...STUDENT_CALENDAR_KEY, 'platform', marathonIds],
    enabled: enrollments.isSuccess,
    queryFn: () => calendarApi.listPlatformEvents({ marathonIds }),
  });

  // Личные записи — видны только самому ученику.
  const personal = useQuery({
    queryKey: [...STUDENT_CALENDAR_KEY, 'personal'],
    queryFn: calendarApi.listPersonalEvents,
  });

  const items: CalItem[] = useMemo(
    () =>
      buildCalItems({
        lessons: lessons.data?.items,
        platform: platform.data,
        personal: personal.data,
        showLive: live,
        showRooms: rooms,
      }),
    [lessons.data, platform.data, personal.data, live, rooms],
  );

  return {
    items,
    /** Живые уроки видны этому ученику — событием, кнопкой «Войти» и запросом. */
    showLive: live,
    /** Разговорные комнаты видны этому ученику. */
    showRooms: rooms,
    // У выключенного запроса уроков `isLoading` — false: экран бесплатника не висит на «Загрузка».
    isLoading: enrollments.isLoading || lessons.isLoading,
    isError: enrollments.isError || lessons.isError || platform.isError || personal.isError,
    error: enrollments.error ?? lessons.error ?? platform.error ?? personal.error,
  };
}
